import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetService, userGroupService } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  Tooltip,
  FormHelperText,
  Chip,
  LinearProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,

  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Key as KeyIcon,
  Api as ApiIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  DeleteForever as DeleteForeverIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';

// Importar constantes e validações compartilhadas
import {
  ASSET_CATEGORIES,
  PHYSICAL_SUBCATEGORIES,
  ASSET_STATUSES,
  MANAGEMENT_TYPES,
  getCategoryInfo,
  getSubcategoryInfo,
  getStatusInfo,
  getFieldOptions,
  isFieldRequired,
  isFieldOptional,
  getAllFieldsForSubcategory,
  validateAssetData,
  validatePhysicalAssetData,
  validateTokenAssetData,
  validateApiAssetData,
  validateLicenseAssetData,
  sanitizeAssetData,
  SUBCATEGORY_ALIASES,
  normalizeSubcategory
} from '../constants/assetConstants';

const AssetForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { category: routeCategory, id } = params;

  // Determinar categoria baseada na rota
  const category = routeCategory || (window.location.pathname.includes('/assets/physical/') ? 'physical' : null);

  const [formData, setFormData] = useState({
    name: '',
    category: category || 'physical',
    subcategory: '',
    description: '',
    owner: '',
    location: '',
    token_value: '',
    api_endpoint: '',
    license_key: '',
    expiry_date: '',
    status: 'active',
    value: '',
    group_id: '',
    metadata: {},
    // Campos específicos para ativos físicos
    in_stock: true,
    with_user: false
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [groups, setGroups] = useState([]);

  // Função para obter ícone baseado na categoria
  const getCategoryIcon = (categoryKey) => {
    const icons = {
      physical: <ComputerIcon />,
      token: <KeyIcon />,
      api: <ApiIcon />,
      license: <AssignmentIcon />
    };
    return icons[categoryKey] || <ComputerIcon />;
  };

  useEffect(() => {
    if (category && ASSET_CATEGORIES[category]) {
      setFormData(prev => ({ ...prev, category }));
    }
  }, [category]);

  useEffect(() => {
    if (id) {
      loadAsset(id);
    }
  }, [id]);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await userGroupService.getUserGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadAsset = async (assetId) => {
    try {
      setLoading(true);
      const response = await assetService.getAsset(assetId, category);
      const asset = response.asset;

      // Mapear subcategorias antigas para novas (compatibilidade)
      let subcategory = asset.subcategory || '';
      if (SUBCATEGORY_ALIASES[subcategory]) {
        subcategory = SUBCATEGORY_ALIASES[subcategory];
      }

      setFormData({
        name: asset.name || '',
        category: asset.category || 'physical',
        subcategory,
        description: asset.description || '',
        owner: asset.owner || '',
        location: asset.location || '',
        token_value: asset.token_value || '',
        api_endpoint: asset.api_endpoint || '',
        license_key: asset.license_key || '',
        expiry_date: asset.expiry_date ? asset.expiry_date.split('T')[0] : '',
        status: asset.status || 'active',
        value: asset.value || '',
        group_id: asset.group_id || '',
        metadata: asset.asset_metadata || {},
        // Campos específicos para ativos físicos
        in_stock: asset.in_stock !== undefined ? asset.in_stock : true,
        with_user: asset.with_user !== undefined ? asset.with_user : false
      });

      setIsEdit(true);
    } catch (error) {
      console.error('Erro ao carregar ativo:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar ativo', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Validação em tempo real
  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    // Validações básicas
    if (name === 'name' && (!value || value.trim().length < 3)) {
      errors.name = 'Nome deve ter pelo menos 3 caracteres';
    } else if (name === 'name') {
      delete errors.name;
    }

    if (name === 'value' && value && isNaN(parseFloat(value))) {
      errors.value = 'Valor deve ser um número válido';
    } else if (name === 'value') {
      delete errors.value;
    }

    // Validações específicas por categoria
    if (formData.category === 'physical' && formData.subcategory) {
      const subcategoryInfo = PHYSICAL_SUBCATEGORIES[formData.subcategory];

      if (subcategoryInfo?.required_fields.includes(name) && (!value || value.trim() === '')) {
        errors[name] = `${name} é obrigatório para ${formData.subcategory}`;
      } else if (name in errors) {
        delete errors[name];
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handler com validação
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: finalValue }));

    // Validar campo em tempo real (apenas para campos não-checkbox)
    if (type !== 'checkbox') {
      validateField(name, finalValue);
    }
  };

  // Obter campos dinâmicos usando as constantes do backend
  const getDynamicFields = () => {
    if (formData.category === 'physical' && formData.subcategory) {
      const subcategoryInfo = PHYSICAL_SUBCATEGORIES[formData.subcategory];
      if (!subcategoryInfo) return [];

      const fields = [];

      // Adicionar campos obrigatórios
      subcategoryInfo.required_fields.forEach(fieldName => {
        let fieldConfig = { name: fieldName, required: true };

        if (fieldName === 'computer_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Computador',
            type: 'select',
            options: subcategoryInfo.computer_types || []
          };
        } else if (fieldName === 'monitor_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Monitor',
            type: 'select',
            options: subcategoryInfo.monitor_types || []
          };
        } else if (fieldName === 'printer_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Impressora',
            type: 'select',
            options: subcategoryInfo.printer_types || []
          };
        } else if (fieldName === 'devices_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Dispositivo',
            type: 'select',
            options: subcategoryInfo.device_types || []
          };
        } else if (fieldName === 'server_type') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tipo de Servidor',
            type: 'select',
            options: subcategoryInfo.server_types || []
          };
        } else if (fieldName === 'manufacturer') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Fabricante',
            type: 'select',
            options: subcategoryInfo.manufacturers || []
          };
        } else if (fieldName === 'size') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Tamanho (polegadas)',
            type: 'number',
            step: '0.1'
          };
        } else if (fieldName === 'memory') {
          fieldConfig = {
            ...fieldConfig,
            label: 'Memória (GB)',
            type: 'number'
          };
        } else {
          fieldConfig = {
            ...fieldConfig,
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' '),
            type: 'text'
          };
        }

        fields.push(fieldConfig);
      });

      // Adicionar campos opcionais comuns
      const optionalFields = ['model', 'serial_number', 'inventory_number', 'network', 'uuid'];
      optionalFields.forEach(fieldName => {
        if (!subcategoryInfo.required_fields.includes(fieldName)) {
          fields.push({
            name: fieldName,
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' '),
            type: 'text',
            required: false
          });
        }
      });

      return fields;
    }

    // Campos para outras categorias
    if (formData.category === 'token') {
      return [
        { name: 'token_value', label: 'Valor do Token', type: 'textarea', required: true }
      ];
    }

    if (formData.category === 'api') {
      return [
        { name: 'api_endpoint', label: 'Endpoint da API', type: 'text', required: true },
        { name: 'owner', label: 'Responsável', type: 'text', required: false }
      ];
    }

    if (formData.category === 'license') {
      return [
        { name: 'license_key', label: 'Chave da Licença', type: 'textarea', required: true },
        { name: 'expiry_date', label: 'Data de Expiração', type: 'date', required: false },
        { name: 'owner', label: 'Responsável', type: 'text', required: false }
      ];
    }

    return [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar todos os campos obrigatórios
    const allFields = getDynamicFields();
    const requiredFields = allFields.filter(f => f.required);
    let hasErrors = false;

    requiredFields.forEach(field => {
      if (!formData[field.name] || formData[field.name].trim() === '') {
        setValidationErrors(prev => ({
          ...prev,
          [field.name]: `${field.label} é obrigatório`
        }));
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        category: formData.category,
        subcategory: formData.subcategory,
        description: formData.description,
        owner: formData.owner,
        location: formData.location,
        status: formData.status,
        value: formData.value ? parseFloat(formData.value) : null,
        group_id: formData.group_id || null,
        metadata: { ...(formData.metadata || {}) }
      };

      // Adicionar campos específicos da categoria
      if (formData.category === 'token') {
        submitData.token_value = formData.token_value;
      } else if (formData.category === 'api') {
        submitData.api_endpoint = formData.api_endpoint;
      } else if (formData.category === 'license') {
        submitData.license_key = formData.license_key;
        submitData.expiry_date = formData.expiry_date;
      } else if (formData.category === 'physical') {
        // Adicionar campos físicos
        allFields.forEach(field => {
          const value = formData[field.name];
          if (value !== undefined && value !== '') {
            if (field.type === 'number') {
              submitData[field.name] = parseFloat(value) || parseInt(value, 10);
            } else {
              submitData[field.name] = value;
            }
          }
        });

        // Adicionar campos de controle específicos para ativos físicos
        submitData.in_stock = formData.in_stock;
        submitData.with_user = formData.with_user;
      }

      if (isEdit && id) {
        await assetService.updateAsset(id, submitData);
        setSnackbar({ open: true, message: 'Ativo atualizado com sucesso!', severity: 'success' });
      } else {
        await assetService.createAsset(submitData);
        setSnackbar({ open: true, message: 'Ativo criado com sucesso!', severity: 'success' });
      }

      setTimeout(() => navigate(`/assets/category/${formData.category}`), 1500);
    } catch (error) {
      console.error('Erro no formulário:', error);
      setSnackbar({
        open: true,
        message: error.message || (isEdit ? 'Erro ao atualizar ativo' : 'Erro ao criar ativo'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const currentCategory = ASSET_CATEGORIES[formData.category];
  const dynamicFields = getDynamicFields();
  const physicalSubcategories = Object.keys(PHYSICAL_SUBCATEGORIES);

  // Loading state
  if (loading && !isEdit) {
    return (
      <Layout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 3 }} />
          <Typography variant="h6">Salvando ativo...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho Melhorado */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>Voltar</Button>
            <Avatar sx={{ bgcolor: `${currentCategory?.color}.main`, mr: 2 }}>
              {getCategoryIcon(formData.category)}
            </Avatar>
            <Box>
              <Typography variant="h4">
                {isEdit ? 'Editar Ativo' : 'Novo Ativo'} - {currentCategory?.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentCategory?.description}
              </Typography>
            </Box>
          </Box>

          {/* Indicador de progresso */}
          {Object.keys(validationErrors).length > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${Object.keys(validationErrors).length} erro(s) de validação`}
              color="warning"
              variant="outlined"
            />
          )}
        </Box>



        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Informações Básicas - Step 1 */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                      <InfoIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Informações Básicas</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nome do Ativo"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Categoria</InputLabel>
                        <Select name="category" value={formData.category} onChange={handleChange} label="Categoria">
                          {Object.entries(ASSET_CATEGORIES).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getCategoryIcon(key)}
                                <Box sx={{ ml: 1 }}>
                                  <Typography variant="body2">{config.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {config.description}
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Status</InputLabel>
                        <Select name="status" value={formData.status} onChange={handleChange} label="Status">
                          {Object.entries(ASSET_STATUSES).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                              <Chip label={config.label} color={config.color} size="small" sx={{ mr: 1 }} />
                              {config.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Descrição"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Descreva detalhadamente o ativo..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Campos Específicos - Step 2 */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                      <ComputerIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Campos Específicos</Typography>
                  </Box>

                  {/* Seletor de Subcategoria para Físicos */}
                  {formData.category === 'physical' && (
                    <Accordion defaultExpanded sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                          Subcategoria: {formData.subcategory || 'Selecione uma subcategoria'}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormControl fullWidth disabled={loading}>
                          <InputLabel>Subcategoria</InputLabel>
                          <Select name="subcategory" value={formData.subcategory} onChange={handleChange} label="Subcategoria">
                            {physicalSubcategories.map(option => {
                              const subcatInfo = PHYSICAL_SUBCATEGORIES[option];
                              return (
                                <MenuItem key={option} value={option}>
                                  <Box>
                                    <Typography variant="body2">{subcatInfo.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {subcatInfo.description}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Render dos campos dinâmicos com validação */}
                  {dynamicFields.length > 0 && (
                    <Grid container spacing={2}>
                      {dynamicFields.map((field) => (
                        <Grid item xs={12} sm={field.type === 'textarea' ? 12 : 6} key={field.name}>
                          {field.type === 'select' ? (
                            <FormControl fullWidth disabled={loading} error={!!validationErrors[field.name]}>
                              <InputLabel>{field.label} {field.required && '*'}</InputLabel>
                              <Select
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleChange}
                                label={`${field.label} ${field.required ? '*' : ''}`}
                              >
                                {(field.options || []).map((option) => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                              {validationErrors[field.name] && (
                                <FormHelperText>{validationErrors[field.name]}</FormHelperText>
                              )}
                            </FormControl>
                          ) : field.type === 'textarea' ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label={`${field.label} ${field.required ? '*' : ''}`}
                              name={field.name}
                              value={formData[field.name] || ''}
                              onChange={handleChange}
                              disabled={loading}
                              error={!!validationErrors[field.name]}
                              helperText={validationErrors[field.name]}
                            />
                          ) : field.type === 'date' ? (
                            <TextField
                              fullWidth
                              type="date"
                              label={`${field.label} ${field.required ? '*' : ''}`}
                              name={field.name}
                              value={formData[field.name] || ''}
                              onChange={handleChange}
                              disabled={loading}
                              error={!!validationErrors[field.name]}
                              helperText={validationErrors[field.name]}
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : field.type === 'number' ? (
                            <TextField
                              fullWidth
                              type="number"
                              label={`${field.label} ${field.required ? '*' : ''}`}
                              name={field.name}
                              value={formData[field.name] || ''}
                              onChange={handleChange}
                              disabled={loading}
                              error={!!validationErrors[field.name]}
                              helperText={validationErrors[field.name]}
                              inputProps={{ step: field.step || '1' }}
                            />
                          ) : (
                            <TextField
                              fullWidth
                              label={`${field.label} ${field.required ? '*' : ''}`}
                              name={field.name}
                              value={formData[field.name] || ''}
                              onChange={handleChange}
                              disabled={loading}
                              error={!!validationErrors[field.name]}
                              helperText={validationErrors[field.name]}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Área de upload de imagens para Consumíveis */}
                  {formData.category === 'physical' && formData.subcategory === 'Consumíveis' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Imagens do Consumível
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<PhotoCameraIcon />}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      >
                        Adicionar Imagens
                        <input
                          hidden
                          multiple
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const files = e.target.files;
                            if (!files || files.length === 0) return;

                            try {
                              // Simular upload - em produção usar assetService.uploadAssetFiles
                              const urls = Array.from(files).map(file => URL.createObjectURL(file));
                              setFormData(prev => ({
                                ...prev,
                                metadata: {
                                  ...(prev.metadata || {}),
                                  pictures: [...(prev.metadata?.pictures || []), ...urls]
                                }
                              }));
                              setSnackbar({ open: true, message: `${files.length} imagem(ns) adicionada(s)!`, severity: 'success' });
                            } catch (err) {
                              setSnackbar({ open: true, message: 'Erro ao adicionar imagens', severity: 'error' });
                            }
                          }}
                        />
                      </Button>

                      {/* Preview das imagens */}
                      {(formData.metadata?.pictures || []).length > 0 && (
                        <ImageList cols={3} rowHeight={120}>
                          {formData.metadata.pictures.map((src, idx) => (
                            <ImageListItem key={`${src}-${idx}`}>
                              <img src={src} alt={`preview-${idx}`} style={{ height: 120, objectFit: 'cover' }} />
                              <ImageListItemBar
                                actionIcon={
                                  <IconButton
                                    sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        metadata: {
                                          ...(prev.metadata || {}),
                                          pictures: (prev.metadata?.pictures || []).filter((_, i) => i !== idx)
                                        }
                                      }));
                                    }}
                                  >
                                    <DeleteForeverIcon />
                                  </IconButton>
                                }
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Informações Adicionais - Step 3 */}
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6">Informações Adicionais</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Valor (R$)"
                        name="value"
                        type="number"
                        step="0.01"
                        value={formData.value}
                        onChange={handleChange}
                        disabled={loading}
                        error={!!validationErrors.value}
                        helperText={validationErrors.value}
                        InputProps={{
                          startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Localização Física"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={loading}
                        InputProps={{
                          startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Responsável"
                        name="owner"
                        value={formData.owner}
                        onChange={handleChange}
                        disabled={loading}
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Grupo</InputLabel>
                        <Select
                          name="group_id"
                          value={formData.group_id}
                          onChange={handleChange}
                          label="Grupo"
                        >
                          <MenuItem value="">
                            <em>Nenhum</em>
                          </MenuItem>
                          {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Campos específicos para ativos físicos */}
                    {formData.category === 'physical' && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Controle de Ativo
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.in_stock}
                                onChange={handleChange}
                                name="in_stock"
                                disabled={loading}
                              />
                            }
                            label="Em estoque"
                          />
                          <FormHelperText>
                            Marque se o ativo está disponível em estoque
                          </FormHelperText>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.with_user}
                                onChange={handleChange}
                                name="with_user"
                                disabled={loading}
                              />
                            }
                            label="Com usuário"
                          />
                          <FormHelperText>
                            Marque se o ativo está atribuído a um usuário
                          </FormHelperText>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Card de Ações e Validação */}
              <Card elevation={2} sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Ações</Typography>

                  {/* Resumo de validação */}
                  <Box sx={{ mb: 2 }}>
                    {Object.keys(validationErrors).length === 0 ? (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                        Todos os campos obrigatórios foram preenchidos!
                      </Alert>
                    ) : (
                      <Alert severity="warning" icon={<WarningIcon />}>
                        {Object.keys(validationErrors).length} campo(s) precisam de atenção
                      </Alert>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={loading || Object.keys(validationErrors).length > 0}
                  >
                    {loading ? 'Salvando...' : 'Salvar Ativo'}
                  </Button>

                  {Object.keys(validationErrors).length > 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      Corrija os erros acima antes de salvar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>

        {/* Snackbar melhorado */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default AssetForm;
